import React, { useEffect, useCallback } from 'react';
import { FiInfo, FiMail } from 'react-icons/fi';
import {
  COLLECTION_METHOD_BANNERS,
  COLLECTION_METHOD_DESCRIPTIONS,
  US_STATES,
} from '../../services/labConstants';
import PatientAddressFields from './PatientAddressFields';
import DrawSiteSelector from './DrawSiteSelector';


export default function StepCollection({
  lab, zip, setZip,
  availResult, availLoading, onCheckAvailability,
  selectedMethod, setSelectedMethod,
  address, setAddress, // { street, apt, city, state, zip }
  addressVerified, setAddressVerified,
  onStateChange = () => {},
  selectedDrawSite, setSelectedDrawSite,
}) {
  const isCombined = Boolean(availResult?.is_combined);
  const serviceableMethods = availResult?.serviceable_methods || [];
  const resolveCollectionOption = useCallback((candidate) => {
    if (!candidate) return null;
    const matched = lab?.collectionOptions?.find((option) =>
      option.method === candidate.method ||
      (candidate.assignment_id && option.assignment_id === candidate.assignment_id)
    );
    return matched ? { ...candidate, ...matched } : candidate;
  }, [lab?.collectionOptions]);

  // For single-method storefronts the collectionOption is already pre-set.
  // Set it automatically on mount if not combined.
  useEffect(() => {
    if (availResult?.is_available && !isCombined && lab?.collectionOptions?.[0] && !selectedMethod) {
      setSelectedMethod(lab.collectionOptions[0]);
    }
  }, [availResult, isCombined, lab, selectedMethod, setSelectedMethod]);

  // Determine methods to render for combined vs single
  const methodsToShow = isCombined
    ? serviceableMethods.map(resolveCollectionOption)
    : (availResult?.is_available && lab?.collectionOptions?.length
        ? lab.collectionOptions.slice(0, 1)   // single-method: show as confirmed card
        : []);

  const showAddressFields = Boolean(selectedMethod);
  const showDrawSites = selectedMethod?.method === 'walk_in_test' || selectedMethod?.method === 'on_site_collection';
  const drawSites = (() => {
    if (!isCombined) return availResult?.drawSites || [];
    const m = serviceableMethods.find(m => m.method === selectedMethod?.method);
    return m?.drawSites || [];
  })();
  const pscCount = (() => {
    if (!isCombined) return availResult?.psc_count || 0;
    const m = serviceableMethods.find(m => m.method === selectedMethod?.method);
    return m?.psc_count || 0;
  })();

  return (
    <div>
      <div className="lcw-card">
        <div className="lcw-card-body">
          <div className="lcw-section-title">Where you'll get tested</div>
          <p className="lcw-section-sub">Enter your ZIP and we'll show the collection options available in your area.</p>

          <div className="lcw-zip-bar">
            <input
              className="lcw-zip-input"
              value={zip}
              onChange={e => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              placeholder="Enter ZIP code"
              maxLength={5}
              onKeyDown={e => { if (e.key === 'Enter') onCheckAvailability(zip); }}
            />
            <button className="lcw-zip-btn" onClick={() => onCheckAvailability(zip)} disabled={availLoading || zip.length < 5}>
              {availLoading ? 'Checking…' : 'Check availability'}
            </button>
          </div>
          {(availResult?.resolved_state || address.state) && (
            <div className="lcw-detected-state">
              <span>Service area detected from ZIP</span>
              <select
                value={availResult?.resolved_state || address.state || ''}
                onChange={e => { if (typeof onStateChange === 'function') onStateChange(e.target.value); }}
                aria-label="Detected patient state"
              >
                {US_STATES.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
              </select>
            </div>
          )}

          {/* Initial state before checking ZIP */}
          {!availResult && !availLoading && (
            <div className="lcw-zip-placeholder">
              Enter a 5-digit ZIP code to see what's available near you.
            </div>
          )}

          {/* Unavailable state */}
          {availResult && !availResult.is_available && (
            <div className="lcw-unavail">
              {availResult.reason || 'This lab is not available in your area.'}
              {availResult.supported_states?.length > 0 && (
                <div className="lcw-unavail-supported">
                  This lab is currently available in: {availResult.supported_states.join(', ')}.
                </div>
              )}
            </div>
          )}

          {/* Available: methods list */}
          {availResult?.is_available && methodsToShow.length > 0 && (
            <>
              <div className="lcw-avail-label">
                Available near {zip}{isCombined ? ' · choose one' : ''}
              </div>

              {methodsToShow.map((opt) => {
                const method = opt.method || opt.collection_method || '';
                const label = opt.label || opt.collection_method_display || method.replace(/_/g, ' ');
                const isSelected = selectedMethod?.method === method || (!isCombined && methodsToShow.length === 1);
                return (
                  <button
                    key={method}
                    className={`lcw-method-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => { setSelectedMethod(resolveCollectionOption(opt)); setSelectedDrawSite(null); }}
                  >
                    <div className="lcw-method-radio">
                      {isSelected && <div className="lcw-method-radio-dot" />}
                    </div>
                    <div>
                      <div className="lcw-method-title">{label}</div>
                      <div className="lcw-method-desc">{COLLECTION_METHOD_DESCRIPTIONS[method] || ''}</div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Method-specific content */}
      {selectedMethod && availResult?.is_available && (
        <>
          {/* Banner for phlebotomy / testkit */}
          {COLLECTION_METHOD_BANNERS[selectedMethod.method] && (
            <div className="lcw-method-banner">
              {selectedMethod.method === 'at_home_phlebotomy' ? <FiMail size={15} /> : <FiInfo size={15} />}
              <span>{COLLECTION_METHOD_BANNERS[selectedMethod.method]}</span>
            </div>
          )}

          {/* Draw site selection for walk_in / on_site */}
          {showDrawSites && (
            <DrawSiteSelector
              drawSites={drawSites}
              pscCount={pscCount}
              zip={zip}
              selectedSite={selectedDrawSite}
              onSelect={setSelectedDrawSite}
            />
          )}

          {/* Address fields for all collection methods. For draw-site methods,
              the location cards intentionally appear first, matching the
              standalone lab checkout flow. */}
          {showAddressFields && (
            <PatientAddressFields
              address={address}
              setAddress={setAddress}
              addressVerified={addressVerified}
              setAddressVerified={setAddressVerified}
              zip={zip}
              availResult={availResult}
              serviceAreaState={availResult?.resolved_state || address.state}
              method={selectedMethod.method}
              onStateChange={onStateChange}
            />
          )}
        </>
      )}
    </div>
  );
}
